import difference from 'lodash/difference';
import type { RouteRecordRaw } from 'vue-router';

import type { SpartenConfig } from '@/features/Sparten/types';
import { stepperStepConfig, validSteps } from '@/router/steps';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { VglError } from '@/services/errorHandler/vglError';
import { getEntries } from '@/utils/objects/objects';

/**
 * Factory function to build all routes of a specific sparten config object.
 *
 * Takes the config of the sparte for each step and merges it with the global config
 * of that step to create vue-router route record object.
 * If enabled in the sparten config it also adds the channel / distribution step
 * for pooling and distribution channel selection.
 *
 * @param spartenConfig A sparten config object
 * @returns RouteRecordRaw array - An array of vue-router route config objects
 */
const parseSpartenConfig = (spartenConfig: SpartenConfig): RouteRecordRaw[] => {
  const spartenName = spartenConfig.props.sparte;
  const distributionSettings = spartenConfig.general.distribution;
  const invalidSteps = difference(Object.keys(spartenConfig.steps), validSteps);

  if (invalidSteps.length > 0) {
    throw new VglError(VGL_ERROR_CODE.ROUTER_INVALID_STEPS, {
      additionalErrorMessage: `Sparte: ${spartenName} - InvalidSteps: ${invalidSteps.join(
        ',',
      )}`,
    });
  }

  const routes: RouteRecordRaw[] = [];

  getEntries(spartenConfig.steps).forEach(([stepName, step]) => {
    routes.push({
      ...stepperStepConfig[stepName],
      ...step,
      name: `${spartenName}.${stepName}`,
    });
  });

  if (
    distributionSettings?.channel === true ||
    distributionSettings?.pooling === true
  ) {
    routes.push({
      name: `${spartenName}.select-distribution`,
      path: 'gesellschaft/:insuranceId/vertriebsweg',
      component: () => import('@/views/Layouts/Distribution/Distribution.vue'),
    });
  }

  return routes;
};

export default parseSpartenConfig;
